<template>
  <div 
    class="tile-card" 
    :class="{ 'non-interactive': !isClickable }"
    @click="goToPage()">
    <div class="tile-header">
      <h2 class="tile-title">{{ pageName }}</h2>
      <v-icon large class="tile-icon">{{ pageIcon }}</v-icon>
    </div>
    
    <div class="tile-content">
      <div v-for="(item, i) in data" :key="i">
        <div v-if="item.loading" class="loading-spinner">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        
        <div v-else class="tile-data">
          <!-- Condensed date/time for appointments -->
          <div v-if="item.date && item.time" class="tile-datetime">
            <span class="date-part">{{ item.date }}</span>
            <span class="time-part">{{ item.time }}</span>
          </div>
          <div v-else-if="item.date" class="tile-date">{{ item.date }}</div>
          <div v-else-if="item.time" class="tile-time">{{ item.time }}</div>
          
          <div v-if="item.desc" class="tile-desc">{{ userInfo?.firstName }}, {{ item.desc }}</div>
          <div v-if="item.desc2" class="tile-desc2">
            {{ item.desc2 }}
            <h4 v-if="item.amount" class="tile-amount">{{ item.amount }}</h4>
          </div>
          <div v-if="item.from" class="tile-from">{{ item.from }}</div>
          
          <!-- Doctor info without bubble for appointments -->
          <div v-if="item.doctor && !item.doctor.includes('No appointments')" class="tile-doctor-simple">{{ item.doctor }}</div>
          
          <!-- Info message for no appointments -->
          <h4 v-if="item.doctor && item.doctor.includes('No appointments')" class="tile-doctor info-message">
            <div class="info-content">
              <v-icon class="info-icon">mdi-information</v-icon>
              <span>{{ item.doctor }}</span>
            </div>
          </h4>
          
          <!-- Enhanced Office Info - simplified -->
          <div v-if="item.officeDesc" class="tile-office-simple">{{ item.officeDesc }}</div>
          <div v-if="item.location && !item.patient" class="tile-location-simple">{{ item.location }}</div>
          <div v-if="item.location2" class="tile-location-simple">{{ item.location2 }}</div>
          <div v-if="item.phone" class="tile-phone-simple">{{ item.phone }}</div>
          
          <h3 v-if="item.subject" class="tile-subject">{{ item.subject }}</h3>
          <h4 v-if="item.patient" class="tile-patient">{{ item.patient }}</h4>
          
          <!-- Location with geo icon for appointments only -->
          <div v-if="item.location && item.patient" class="tile-location-with-icon">
            <v-icon class="location-icon">mdi-map-marker-outline</v-icon>
            <span>{{ item.location }}</span>
          </div>
          <h4 v-if="item.alert" class="tile-alert enhanced-alert">
            <div class="alert-content">
              <v-icon class="alert-icon">mdi-alert-circle</v-icon>
              <span>{{ item.alert }}</span>
            </div>
          </h4>
          <div v-if="item.body" class="tile-body">{{ item.body }}</div>
          
          <!-- Single button logic to prevent duplicates -->
          <v-btn v-if="item.btn" class="btn-primary">{{ item.btn }}</v-btn>
          <v-btn v-else-if="item.schedule" class="btn-primary">Schedule Now</v-btn>
          <v-btn v-else-if="item.print" class="btn-primary">Print</v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'Notification',
  props: ['tile_data'],
  components: {},
  data() {
    return {
      userInfo: '',
      tabSelect: '',
      componentKey: 0,
    };
  },
  computed: {
    isClickable() {
      return this.data && this.data[0] && (this.data[0].page || this.data[0].action);
    },
  },
  watch: {
    tile_data: {
      handler() {
        this.setTileData();
        this.componentKey += 1;
      },
      deep: true,
    },
  },
  methods: {
    setTileData() {
      this.pageName = this.tile_data.title;
      this.pageIcon = this.tile_data.icon;
      this.data = this.tile_data.data;
      this.loading = this.tile_data.data.loading;
      this.userInfo = JSON.parse(localStorage.getItem('user'));
    },
    goToPage() {
      if (!this.isClickable) return;
      
      if (this.data[0].action) {
        this.data[0].action();
      } else {
        this.$router.push({
          name: this.data[0].page,
          query: { tab: this.data[0].tab },
        });
      }
    },
  },
  created() {
    this.setTileData();
  },
  mounted() {
    this.setTileData();
  },
};
</script>
<style lang="scss" scoped>
@import '@/scss/variables.scss';

// Component-specific colors
$tile-bg-color: #ffffff;
$tile-text-color: #2d3748;
$tile-border-color: #e2e8f0;
$tile-icon-color: #6b7280;
$tile-secondary-text-color: #718096;
$tile-accent-text-color: #4a5568;
$tile-time-bg-color: #edf2f7;
$tile-info-bg-color: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
$tile-info-border-color: #bae6fd;
$tile-info-icon-color: #0284c7;
$tile-alert-bg-color: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
$tile-alert-border-color: #fecaca;
$tile-alert-icon-color: #dc2626;
$tile-alert-text-color: #f56565;

// Force shadows to show
.v-card, .v-sheet {
  box-shadow: none !important;
}

.tile-card {
  background-color: $tile-bg-color;
  border-radius: 12px;
  height: 300px;
  padding: 32px 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  color: $tile-text-color;
  box-shadow: $shadow-md !important;
  border: 1px solid $tile-border-color;
  transition: transform $transition-normal $easing-smooth, box-shadow $transition-normal $easing-smooth;
  transform: translate3d(0, 0, 0);
  
  &.non-interactive {
    cursor: default;
    
    &:hover {
      transform: none;
      box-shadow: $shadow-md !important;
    }
  }
  
  &:hover {
    transform: translate3d(0, $transform-lift, 0);
    box-shadow: $shadow-lg !important;
  }
}

.tile-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  
  .tile-title {
    font-size: 20px;
    font-weight: 600;
    color: $tile-text-color;
    margin: 0;
    text-align: left;
    flex: 1;
    
    @media (max-width: $breakpoint-mobile) {
      font-size: 18px;
    }
  }
  
  .tile-icon {
    font-size: 32px;
    color: $tile-icon-color;
    margin-left: 12px;
  }
}

.tile-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  // Tighter spacing for most tiles
  .tile-office-simple:first-child,
  .tile-desc:first-child,
  .tile-desc2:first-child,
  .tile-subject:first-child,
  .info-message:first-child,
  .enhanced-alert:first-child {
    margin-top: -8px;
  }
}

.tile-data {
  // Base text elements
  .tile-date, .tile-time, .tile-desc, .tile-desc2, 
  .tile-from, .tile-office, .tile-location, .tile-phone, .tile-body {
    font-size: 14px;
    color: $tile-secondary-text-color;
    margin-bottom: 8px;
  }
  
  // Date/Time display
  .tile-datetime {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    
    .date-part {
      font-size: 16px;
      font-weight: 600;
      color: $tile-text-color;
    }
    
    .time-part {
      font-size: 15px;
      color: $tile-text-color;
      background: $tile-time-bg-color;
      padding: 6px 10px;
      border-radius: 6px;
      font-weight: 600;
    }
  }
  
  // Doctor and patient info
  .tile-doctor, .tile-patient {
    font-weight: 600;
    color: $tile-accent-text-color;
    margin-bottom: 8px;
    font-size: 16px;
    line-height: 1.4;
  }
  
  .tile-doctor-simple {
    font-size: 14px;
    color: $tile-accent-text-color;
    margin-bottom: 8px;
  }
  
  // Location with icon
  .tile-location-with-icon {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: $tile-accent-text-color;
    margin-bottom: 8px;
    
    .location-icon {
      font-size: 16px;
      color: $tile-icon-color;
    }
  }
  
  // Office information
  .tile-office-simple {
    font-size: 16px;
    color: $tile-text-color;
    font-weight: 600;
    margin-bottom: 8px;
  }
  
  .tile-location-simple, .tile-phone-simple {
    font-size: 14px;
    color: $tile-accent-text-color;
    margin-bottom: 8px;
  }
  
  // Message types
  .info-message {
    background: $tile-info-bg-color;
    border: 1px solid $tile-info-border-color;
    border-radius: 8px;
    padding: 12px;
    margin: 16px 0;
    font-weight: 400;
    font-size: 14px;
    
    .info-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .info-icon {
      color: $tile-info-icon-color;
      font-size: 18px;
    }
  }
  
  .enhanced-alert {
    background: $tile-alert-bg-color;
    border: 1px solid $tile-alert-border-color;
    border-radius: 8px;
    padding: 12px;
    margin: 16px 0;
    
    .alert-content {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .alert-icon {
      color: $tile-alert-icon-color;
      font-size: 18px;
    }
  }
  
  // Content elements
  .tile-subject {
    font-size: 16px;
    font-weight: 600;
    color: $tile-accent-text-color;
    margin: 8px 0;
  }
  
  .tile-amount {
    font-size: 24px;
    font-weight: bold;
    color: $tile-text-color;
    margin: 8px 0;
  }
  
  .tile-alert {
    color: $tile-alert-text-color;
    font-weight: 500;
    margin: 12px 0;
    
    .v-icon {
      margin-right: 8px;
    }
  }
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}
</style>
